import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { airports } from "@/data/airports";
import { blogPosts } from "@/data/blogPosts";

interface RelatedContentProps {
  type: "airport" | "blog";
  currentId?: string;
  relatedKeywords?: string[];
  maxItems?: number;
}

export const RelatedContent = ({ 
  type, 
  currentId, 
  relatedKeywords = [], 
  maxItems = 3 
}: RelatedContentProps) => {
  
  if (type === "airport") {
    // Find related blog posts for an airport
    const relatedPosts = blogPosts
      .filter(post => {
        const keywords = [...post.keywords, post.title.toLowerCase()];
        return relatedKeywords.some(keyword => 
          keywords.some(k => k.includes(keyword.toLowerCase()))
        );
      })
      .slice(0, maxItems);

    if (relatedPosts.length === 0) return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Related Travel Tips</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {relatedPosts.map(post => (
            <Link 
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="block group hover:bg-muted/50 p-3 rounded-lg transition-colors"
            >
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-2">
                {post.title}
                <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {post.description}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {post.readTime} • {post.date}
              </p>
            </Link>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Find related airports for a blog post
  const relatedAirports = airports
    .filter(airport => 
      relatedKeywords.some(keyword => 
        airport.code.toLowerCase().includes(keyword.toLowerCase()) ||
        airport.name.toLowerCase().includes(keyword.toLowerCase()) ||
        airport.city.toLowerCase().includes(keyword.toLowerCase())
      )
    )
    .slice(0, maxItems);

  if (relatedAirports.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Related Airports</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {relatedAirports.map(airport => (
          <Link 
            key={airport.code}
            to={`/airports/${airport.code.toLowerCase()}`}
            className="block group hover:bg-muted/50 p-3 rounded-lg transition-colors"
          >
            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-2">
              {airport.code} - {airport.name}
              <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {airport.city}
            </p>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
};
